import { jsx, jsxs } from "react/jsx-runtime";
import react from "react";
import lite from "clsx/lite";
import { getPurinaUrl, sendIt } from "../../../common/functions.mjs";
import { Auth0IdentityProvider, createAuthClient } from "@nestle-npus-nbm/purina-auth";
import Pets from "../../icons/Pets.mjs";
import Rewards from "../../icons/Rewards.mjs";
import SSO from "../../icons/SSO.mjs";
import { Heading } from "../Heading/Heading.mjs";
import IconList from "../IconList/IconList.mjs";
import { Greeter } from "../Greeter/Greeter.mjs";
import { NavItem } from "../NavItem/NavItem.mjs";
import { SocialLoginButton } from "../SocialLoginButton/SocialLoginButton.mjs";
import UnreadOffersCount from "../UnreadOffers/UnreadOffersCount.mjs";
import { useGetAuth0User } from "../../hooks/index.mjs";
const DrawerContents = ({ user, config, unreadOffersCount, btnLayout, openLinksInNewTab = false })=>{
    const Auth = react.useMemo(()=>createAuthClient(config.auth0), [
        config
    ]);
    const [aid, setAid] = react.useState();
    const { ansiraUuid } = useGetAuth0User(config);
    react.useEffect(()=>{
        if (ansiraUuid) setAid(ansiraUuid);
    }, [
        ansiraUuid
    ]);
    const analyticsEventHandlers = config.analyticsEventHandlers;
    const primarySlots = (config.pup.primarySlots ? config.pup.primarySlots : []).slice(0, 4);
    const params = `?property=${config.pup.property}&origin=${encodeURIComponent(window.location.href)}&client=${config.auth0.clientId}&aid=${aid}&method=${config.pup.method}&sign-up-source=${config.analytics?.signUpSourceCode}`;
    const navItems = [
        {
            name: 'Purina Rewards',
            config,
            href: `${config.pup.url}/${params}`,
            description: 'Earn points for signing up, buying & reviewing Purina products',
            openNewTab: openLinksInNewTab,
            subNavDefaultOpen: !openLinksInNewTab,
            ...!openLinksInNewTab && {
                subNavItems: [
                    {
                        href: `${config.pup.url}/${params}`,
                        name: 'About Purina Rewards',
                        config,
                        description: 'Earn points for signing up, buying & reviewing Purina products',
                        openNewTab: openLinksInNewTab
                    },
                    {
                        href: `${getPurinaUrl(config.pup.url)}/mypurina-app/receipt-upload`,
                        name: 'Upload Receipt',
                        config,
                        openNewTab: openLinksInNewTab
                    },
                    {
                        href: `${getPurinaUrl(config.pup.url)}/mypurina-app/rewards-catalog`,
                        name: 'Rewards Catalog',
                        config,
                        openNewTab: openLinksInNewTab
                    }
                ]
            }
        },
        {
            name: 'My Inbox',
            description: "See what's new",
            href: `${config.pup.url}/inbox${params}`,
            config,
            openNewTab: openLinksInNewTab,
            navItemIcon: /*#__PURE__*/ jsx(UnreadOffersCount, {
                unreadOffersCount: unreadOffersCount
            })
        },
        {
            name: 'My Pets',
            description: 'Check-in on your pets',
            href: `${config.pup.url}/pets${params}`,
            config,
            openNewTab: openLinksInNewTab
        },
        {
            name: 'My Account',
            description: 'Update your profile & account settings',
            href: `${config.pup.url}/account${params}`,
            config,
            openNewTab: openLinksInNewTab
        },
        {
            name: 'Privacy & Legal Info',
            description: 'Learn more',
            href: `${config.pup.url}/privacy${params}`,
            config,
            openNewTab: true
        }
    ];
    const iconList = [
        {
            icon: /*#__PURE__*/ jsx(Rewards, {
                className: "ptw-mr-3 ptw-min-w-[48px]"
            }),
            description: 'Earn points for signing up, buying & reviewing Purina products, then redeem points for treats and toys.'
        },
        {
            icon: /*#__PURE__*/ jsx(SSO, {
                className: "ptw-mr-3 ptw-min-w-[48px]"
            }),
            description: "Use a single account to access Purina.com, Petfinder, myPurina and more."
        },
        {
            icon: /*#__PURE__*/ jsx(Pets, {
                className: "ptw-mr-3 ptw-min-w-[48px]"
            }),
            description: 'Keep all your pet info handy in one place for easier access.'
        }
    ];
    if (!user) return /*#__PURE__*/ jsxs("div", {
        className: "ptw-grid ptw-h-[calc(100dvh-80px)] ptw-grid-rows-[1fr_auto]",
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: "ptw-relative ptw-flex ptw-flex-col ptw-overflow-y-auto",
                tabIndex: 0,
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: "ptw-p-4",
                        children: /*#__PURE__*/ jsx(Heading, {
                            as: "h2",
                            id: "radix-:r17:",
                            size: "titleSmall",
                            weight: "heavy",
                            className: "ptw-mb-4 ptw-mt-0",
                            children: config.pup.headerTitle ?? 'Create your Purina account and fetch your rewards'
                        })
                    }),
                    /*#__PURE__*/ jsx("img", {
                        alt: "",
                        src: config.pup.image ? config.pup.image : "https://live-purina-h20.pantheonsite.io/sites/default/files/2023-06/mypurina_promo_module.jpg"
                    }),
                    /*#__PURE__*/ jsx(IconList, {
                        list: config.pup.iconList ?? iconList
                    })
                ]
            }),
            /*#__PURE__*/ jsxs("div", {
                className: "ptw-mb-[65px] ptw-w-full ptw-max-w-[360px] ptw-border-t-surface-line ptw-bg-surface-base",
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: lite('ptw-flex ptw-gap-3 ptw-p-4', 'vertical' === btnLayout ? 'ptw-flex-col' : 'ptw-flex-row'),
                        children: [
                            /*#__PURE__*/ jsx("button", {
                                className: "ptw-w-full ptw-cursor-pointer ptw-rounded ptw-border-none ptw-bg-primary-base ptw-p-4 ptw-text-body-md ptw-text-primary-text hover:ptw-bg-primary-active disabled:ptw-bg-disabled-base disabled:ptw-text-disabled-text",
                                onClick: ()=>{
                                    if (analyticsEventHandlers?.handleCreateAccountEvent) analyticsEventHandlers?.handleCreateAccountEvent();
                                    else sendIt({
                                        event: 'create_account_click',
                                        eventParams: {
                                            content_name: `create account ${config.analytics?.formParent ?? '[not set]'}`,
                                            cta_location: 'pup native modal',
                                            form_brand: config.analytics?.formBrand,
                                            internal_campaign_id: config.analytics?.formCampaignId ?? '[not set]',
                                            origin_site_id: config.analytics?.formBrand ?? '[not set]',
                                            form_id: config.analytics?.signUpSourceCode ?? '[not set]',
                                            form_version: 'v1'
                                        }
                                    }, config.pup.dataLayerName, config.pup.logging);
                                    setTimeout(()=>{
                                        Auth.signUp({
                                            appState: {
                                                returnTo: window.location.href
                                            }
                                        });
                                    }, 100);
                                },
                                children: config.pup.createAccountButtonText ? config.pup.createAccountButtonText : 'Create Account'
                            }),
                            /*#__PURE__*/ jsx("button", {
                                className: "ptw-border-radius-base ptw-w-full ptw-cursor-pointer ptw-rounded ptw-border-medium ptw-border-solid ptw-border-surface-text ptw-bg-utils-transparent ptw-p-4 ptw-text-body-md ptw-text-surface-text hover:ptw-bg-surface-text hover:ptw-text-primary-text",
                                onClick: ()=>{
                                    if (analyticsEventHandlers?.handleLoginEvent) analyticsEventHandlers.handleLoginEvent();
                                    else sendIt({
                                        event: 'form_start',
                                        eventParams: {
                                            form_name: 'login',
                                            form_type: 'login',
                                            form_brand: config.analytics?.formBrand ?? '[not set]',
                                            form_id: config.analytics?.loginSourceCode ?? '[not set]',
                                            form_version: 'v1'
                                        }
                                    }, config.pup.dataLayerName, config.pup.logging);
                                    setTimeout(()=>{
                                        Auth.federatedSignIn({
                                            appState: {
                                                returnTo: window.location.href
                                            }
                                        });
                                    }, 100);
                                },
                                children: "Sign in"
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: "ptw-flex ptw-items-center ptw-px-4",
                        children: [
                            /*#__PURE__*/ jsx("div", {
                                className: "ptw-h-[1px] ptw-flex-grow ptw-bg-surface-line"
                            }),
                            /*#__PURE__*/ jsxs("div", {
                                className: "ptw-mx-4 ptw-flex-shrink ptw-text-disabled-text",
                                children: [
                                    "or ",
                                    /*#__PURE__*/ jsx("span", {
                                        className: "ptw-hidden md:ptw-inline",
                                        children: "continue with"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ jsx("div", {
                                className: "ptw-h-[1px] ptw-flex-grow ptw-bg-surface-line"
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: "ptw-grid ptw-gap-2 ptw-p-4 md:ptw-grid-cols-3",
                        children: [
                            /*#__PURE__*/ jsx(SocialLoginButton, {
                                socialProvider: Auth0IdentityProvider.Google,
                                config: config,
                                showProviderText: false
                            }),
                            /*#__PURE__*/ jsx(SocialLoginButton, {
                                socialProvider: Auth0IdentityProvider.Apple,
                                config: config,
                                showProviderText: false
                            }),
                            /*#__PURE__*/ jsx(SocialLoginButton, {
                                socialProvider: Auth0IdentityProvider.Facebook,
                                config: config,
                                showProviderText: false
                            })
                        ]
                    })
                ]
            })
        ]
    });
    return /*#__PURE__*/ jsxs("div", {
        className: "ptw-p-4",
        children: [
            /*#__PURE__*/ jsx(Greeter, {
                user: user,
                config: config
            }),
            primarySlots && primarySlots.map((item)=>/*#__PURE__*/ jsx("div", {
                    className: "ptw-my-3",
                    children: /*#__PURE__*/ jsx(NavItem, {
                        config: config,
                        href: item.href,
                        description: item.description,
                        name: item.title,
                        openNewTab: item.openNewTab,
                        asPrimary: true
                    })
                }, item.href)),
            /*#__PURE__*/ jsxs("nav", {
                className: "ptw-rounded-[12px] ptw-border-thin ptw-border-solid ptw-border-surface-light",
                children: [
                    navItems.map((item)=>/*#__PURE__*/ jsx(NavItem, {
                            config: config,
                            href: item.href,
                            name: item.name,
                            subNavItems: item.subNavItems,
                            subNavDefaultOpen: item.subNavDefaultOpen,
                            description: item.description,
                            openNewTab: item.openNewTab,
                            navItemIcon: item.navItemIcon
                        }, item.href)),
                    /*#__PURE__*/ jsx("button", {
                        onClick: ()=>{
                            if (analyticsEventHandlers?.handleLogoutEvent) analyticsEventHandlers.handleLogoutEvent();
                            else {
                                sendIt({
                                    event: 'nav_click',
                                    eventParams: {
                                        nav_item: 'logout',
                                        nav_type: 'account'
                                    }
                                }, config.pup.dataLayerName, config.pup.logging);
                                sendIt({
                                    event: 'logout'
                                }, config.pup.dataLayerName, config.pup.logging);
                            }
                            setTimeout(()=>{
                                Auth.signOut(window.location.origin);
                            }, 100);
                        },
                        className: "ptw-flex ptw-w-full ptw-cursor-pointer ptw-items-center ptw-justify-between ptw-border-none ptw-bg-utils-transparent ptw-p-4 ptw-text-body-md",
                        children: /*#__PURE__*/ jsx("div", {
                            className: "ptw-flex ptw-flex-col",
                            children: /*#__PURE__*/ jsx("div", {
                                className: "ptw-font-bold",
                                children: "Sign out"
                            })
                        })
                    })
                ]
            })
        ]
    });
};
export { DrawerContents };
